export type Role = "owner" | "admin" | "operator" | "member";

export interface User {
    id: string;
    email: string;
    key: string;
    passwordExpiry: string | null;
    firstName?: string;
    lastName?: string;
    displayName?: string;
    roles?: Role[];
    createdAt?: string;
    updatedAt?: string;
    lastLoginAt?: string;
    isActive?: boolean;
    profilePicture?: string;
    phoneNumber?: string;
}

export interface UserProfile {
    id: number;
    firstName?: string;
    lastName?: string;
    avatar?: string | null;
    notes?: string | null;
    bloodType?: string | null;
    gender?: string | null;
    height?: number | null;
    birthday?: string | null;
    isFirstAidCertified: boolean;
    mobility?: string | null;
    emergencyContacts: EmergencyContact[];
}

export interface EmergencyContact {
    id: number | null;
    displayName: string;
    email: string;
    phone: string;
    relationship: string;
    profileId: number | null;
}

export interface UserPhoneInfo {
    mobileNumber: string;
    receiveSms: boolean;
    /** Set by the server once the number has been verified via SMS link. */
    active?: boolean;
}

// Body of POST /users/{id}/profile. Mirrors the iOS app, which sends every key.
export interface ProfileUpdateRequest {
    userId: number;
    firstName: string;
    lastName: string;
    displayName: string;
    avatar: string | null;
    gender: string | null;
    height: number | null;
    notes: string;
    birthday: string | null;
    bloodType: string | null;
    isFirstAidCertified: boolean;
    mobility: string | null;
    emergencyContacts: EmergencyContact[] | null;
    phoneNumbers: Omit<UserPhoneInfo, "active">[] | null;
}

export interface AvatarUploadResponse {
    id: number;
    profile: { avatar: string };
}

export interface Community {
    id: number;
    avatar?: string;
    displayName: string;
    orgName: string;
    description?: string;
    isPublic: boolean;
    orgUrl?: string;
    orgApiVersion?: string;
    orgMapSlug?: string;
    orgId?: string;
    serviceToken?: string;
    uniqueId?: string; // Optional for new communities not yet synced with EdgeNode
    serviceRefreshToken?: string;
    serviceTokenExpiry?: number;
    domainName?: string;
    ownerId: number;
    autoInvite?: boolean;
    autoInviteMembers?: boolean; // API uses this variant too
    createdAt?: string;
    updatedAt?: string;
    // Nested data from API response
    venues?: VenueAdmin[];
    members?: Membership[];
}

/**
 * Community role types - used in both Consumer API and EdgeNode API
 * Note: "Owner" is used in Consumer API admin context
 *       "EndUser" is legacy and equivalent to "Member"
 */
export type CommunityRole =
    | "Owner"
    | "Admin"
    | "Operator"
    | "Security"
    | "Member"
    | "EndUser"
    | "Installer"
    | "FirstResponder"
    | "Supervisor";

export interface Membership {
    id: number;
    userId?: number;
    communityId?: number;
    joinedAt: string;
    uniqueId?: string; // EdgeNode sync ID
    externalToken?: string;
    externalId?: string;
    externalTokenExpiry?: number;
    externalRefreshToken?: string;
    communityRole: CommunityRole;
    community: Community;
    user?: {
        id: number;
        email: string;
        displayName: string;
        firstName?: string;
        lastName?: string;
        avatar?: string;
        /** Strict `=== false` gating in MembersList.vue; `undefined` renders no chip. */
        claimed?: boolean;
    };
    /**
     * Group memberships as returned by the members query. Venue membership is
     * modelled as membership of that venue's group, so this is also how the
     * edit dialog derives which venues a member currently belongs to.
     */
    groups?: UserListGroup[];
}

export interface CurrentUserResponse {
    id: number;
    displayName: string;
    email: string;
    active: boolean;
    passwordExpiry: string | null;
    key: string;
    role: string;
    profile: UserProfile;
    memberships: Membership[];
    devices: any[];
    phoneInfo?: UserPhoneInfo[];
}

export interface LoginRequest {
    email: string;
    password: string;
    device?: Device;
}

export interface LoginResponse {
    id: number;
    key: string;
    email: string;
    passwordExpiry: string | null;
    token: string;
}

export interface Device {
    uniqueId: string;
    os: string;
    displayName: string;
    version: string;
    mobileNumber: string;
    pushToken: string;
    userId: number;
}

export interface ForgotPasswordRequest {
    email: string;
}

export interface RegisterRequest {
    email: string;
    displayName: string;
    firstName: string;
    lastName: string;
    password: string;
    mobileNumber?: string;
}

// New interface for token-based password change (matches PasswordChangeDto from API)
export interface PasswordChangeWithTokenRequest {
    password: string;
}

export interface AccountVerificationResponse {
    message?: string;
    success?: boolean;
}

export interface ResendVerificationRequest {
    email: string;
}

// The API answers identically whether or not the address has a pending account,
// so `message` is a generic acknowledgement, never a per-account confirmation.
export interface ResendVerificationResponse {
    message?: string;
}

// ================================================================
// EdgeNode V2 API Types
// ================================================================

/**
 * Base type for GeoJSON Point objects used in V2 API responses.
 */
export interface GeoJsonPoint {
    type: "Point";
    coordinates: {
        latitude: number;
        longitude: number;
    };
}

// V2 EdgeNode API Types
export interface EdgeDevice {
    /** Composite string ID, e.g. "camera:14" — consistent across REST and MQTT */
    id: string;
    /** Numeric database ID */
    dbId: number;
    name: string;
    type: string;
    lat: number;
    long: number;
    updatedAt: string;
    createdAt: string;
    levelId: number | null;
    buildingId: number | null;
    venueId: number | null;
    spaceId: number | null;
    exposureLevel: number;
    status: string; // Device status: "online", "offline", "error", "unconfigured"
    // Additional fields that might be present
    spaceName?: string;
    operationalState?: string;
    adminStateOn?: boolean;
}

// V2 Device List Response (matches actual API response structure)
export interface DeviceListResponse {
    page: number;
    pageSize: number;
    total: number;
    levelId?: number;
    type?: string[];
    data: EdgeDevice[];
}

// ================================================================
// Beacon Placement Location Types
// ================================================================

/**
 * Main entity for beacon placement locations (returned from GET requests).
 * Represents a planned location for beacon installation.
 */
export interface BeaconPlacementLocation {
    id: number;
    lat: number; // GPS latitude coordinate
    long: number; // GPS longitude coordinate
    spaceId?: number; // Space ID from Edge DB
    spaceName?: string; // Space name for display
    spaceKey?: string; // MappedIn space key
    levelId?: number; // Level ID from Edge DB
    buildingId?: number; // Building ID from Edge DB
    venueId: number; // Venue ID from Edge DB
    type?: "trackingBeacon" | "masterBeacon"; // Beacon type
    createdAt?: string;
    updatedAt?: string;
}

/**
 * DTO for creating new beacon placement locations.
 * Matches CreateBeaconPlacementLocationDto from Edge API.
 */
export interface CreateBeaconPlacementLocationDto {
    lat: number; // Required
    long: number; // Required
    levelId?: number; // Optional
    venueId: number; // Required
    type?: "trackingBeacon" | "masterBeacon";
}

/**
 * DTO for updating existing beacon placement locations.
 * Matches UpdateBeaconPlacementLocationDto from Edge API.
 */
export interface UpdateBeaconPlacementLocationDto {
    lat?: number;
    long?: number;
    levelId?: number;
    venueId?: number;
    beaconId?: number; // Set to link a tracking beacon to this location
}

// Generate endpoints use path parameters (spaceId, levelId, venueId)
// No request body needed - see useBeaconPlacementApi.ts

export interface EdgeUser {
    id: number;
    key: string;
    tokenVersion: number;
    displayName: string;
    firstName?: string;
    lastName?: string;
    exposureLevel?: number; // Numeric exposure level (1-4, 98, 99)
    exposureSource?: string;
    safezoneMsgSent?: boolean;
    communityRole: string;
    active: boolean;
    isFirstAidCertified?: boolean;
    hasMobileApp?: boolean;
    isSMSEnabled?: boolean;
    updatedAt: string;
    lastSync: string;
    lastLocationPost?: string | null; // Timestamp of last location update (added via MQTT/streaming)
    spaceId?: number | null;
    levelId?: number;
    buildingId?: number | null;
    venueId?: number;
    incidentId?: number | null;
    lat?: number;
    lng?: number; // V2 API uses 'lng' not 'long'
    wearable?: any | null; // Wearable device data (SOS button, etc.)
    // User profile fields (included in detailed user response)
    email?: string;
    avatar?: string | null;
    notes?: string | null;
    bloodType?: string | null;
    gender?: string | null;
    height?: number | null;
    birthday?: string | null;
    mobility?: string | null;
    emergencyContacts?: Array<{
        displayName: string;
        email: string;
        phone: string;
        relationship: string;
    }>;
    phoneInfo?: Array<{
        mobileNumber: string;
        receiveSms: boolean;
    }>;
    // Legacy/compatibility fields (may be present in some responses)
    sosButton?: {
        sosPressed: boolean;
    };
    sosPressed?: boolean; // Flat SOS status
    presenceLevel?: number;
    assisting?: boolean;
    /** Consumer group memberships in this edge's community, only on `/users/:id/details`. */
    groups?: EdgeUserGroup[];
}

export interface EdgeUserGroup {
    id: number;
    displayName: string;
    communityId: number;
    venueId: number | null;
    responderType: ResponderType | null;
    /** Consumer community uniqueId (the edge's UNIQUE_ID). */
    communityUniqueId: string | null;
}

// Telemetry fields come from the master beacon wearable healthcheck.
export interface Wearable {
    id: number;
    macAddress: string;
    wearableId: string; // "<major>.<minor>"
    major: number;
    minor: number;
    sos: "on" | "off";
    userKey: string | null;
    active: boolean;
    isLostOrStolen: boolean;
    userId: number | null;
    status?: "online" | "offline" | null;
    battery?: number | null; // percent, -1 until first telemetry
    firmwareVersion?: string | null;
    model?: string | null;
    reportedState?: string | null; // Track, Presence, SOS, Factory or unknown
    lastSeenAt?: string | null;
    lastSeenBy?: string | null; // MAC of the reporting master beacon
    health?: Record<string, unknown> | null;
    updatedAt?: string;
}

// `GET /wearables/summary`; counts cover all wearables, not one venue.
export interface WearableSummary {
    totalRecords: number;
    status: { online: number; offline: number };
    // lowAssigned: low-battery wearables assigned to a user; absent on older backends.
    battery: { low: number; lowAssigned?: number; unknown: number; threshold: number };
}

// Row of the paginated `GET /wearables` list.
export interface WearableListItem extends Wearable {
    user: {
        id: number;
        key: string;
        firstName?: string;
        lastName?: string;
        displayName?: string;
        communityRole?: string;
    } | null;
}

export interface Venue {
    id: number;
    name: string;
    address: string;
    slug: string;
    mappedInId: string;
    externalMapId: string | null;
    organizationId: number;
    defaultVenue: boolean;
    lat: number;
    lng: number;
    /**
     * Map engine that renders this venue (milestone M0.2; absent = "mappedin").
     * See src/map-engine: "corridor" renders with corridor-map-sdk (M2.1).
     */
    mapEngine?: "mappedin" | "corridor";
}

export interface Level {
    id: number;
    name: string;
    mappedInId: string;
    defaultLevel?: boolean;
    orderNumber?: number | null;
    externalMapId?: string | null;
    buildingId?: number;
    venueId: number;
    elevation?: number;
    lat: number;
    lng: number;
    mapZoomValue?: number;
    mapZoomIncrement?: number;
    spaceZoomThreshold?: number;
    buildZoomThreshold?: number;
    maxZoom?: number;
    minZoom?: number;
    /**
     * Metres per unit of the MappedIn floor plan the level was drawn from. The
     * zoom fields above are MappedIn camera distances in those units. Filled by
     * the edge server from MappedIn; null until then.
     */
    mapScale?: number | null;
}

export interface Building {
    id: number;
    name: string;
    venueId: number;
    mappedInId: string;
    orderNumber: number | null;
    lat?: number;
    lng?: number;
}

export interface Space {
    id: number;
    name: string | null;
    mappedInId: string;
    lat: number;
    lng: number;
    levelId: number;
    buildingId: number;
    venueId: number;
}

// V2 API Response Structures
// Paginated response for devices, incidents, safezones list
export interface V2PaginatedResponse<T> {
    page: number;
    pageSize: number;
    total: number;
    data: T[];
    // Additional filters might be included
    [key: string]: any;
}

// Paginated safezone response
export interface V2SafezoneListResponse extends V2PaginatedResponse<Safezone> {}

// Single safezone read response structure (V2 API)
export interface V2SafezoneReadResponse {
    safeZone: Safezone;
    incidentTypes: string[];
}

// Single incident response structure (V2 API)
export interface V2IncidentResponse {
    incident: Incident & {
        level?: Level;
    };
    markers: Marker[];
    comments: IncidentComment[];
}

export interface Marker {
    id: number;
    createdAt: string;
    updatedAt: string;
    name: string | null;
    type: string;
    description: string | null;
    lat: number;
    long: number;
    levelId: number;
    incidentId: number;
    markerCount: number | null;
    defaultName: string | null;
    status?: string; // "confirmed" | "unconfirmed" | "resolved"
}

export interface IncidentComment {
    id: number;
    createdAt: string;
    updatedAt: string;
    incidentId: number;
    enteredById: number;
    enteredByName: string;
    content: string;
    incident?: {
        id: number;
        status: string;
    };
}

export interface SafeZoneIncidentType {
    id: number;
    createdAt: string;
    updatedAt: string;
    safeZoneId: number;
    incidentType: string;
}

export interface Safezone {
    id: number;
    createdAt: string;
    updatedAt: string;
    name: string;
    archived: boolean;
    isArchivable: boolean;
    radius: number;
    centrePointLat: number;
    centrePointLong: number;
    spaceId: number | null;
    levelId: number;
    venueId: number;
    description: string | null;
    exposure: string | null;
    safeZoneIncidentTypes: SafeZoneIncidentType[];
}

export interface IncidentZone {
    id: number;
    createdAt: string;
    updatedAt: string;
    incidentId: number;
    exposure: string;
    exposureLevel: number;
    levelId: number;
}

export interface IncidentSafeZone {
    id: number;
    createdAt: string;
    updatedAt: string;
    incidentId?: number;
    safeZoneId?: number;
    levelId: number | null;
    safezone: Safezone;
}

/**
 * End user assisting record - tracks which users are responding to an incident
 * V2 API now includes full endUser details with location information
 */
export interface EndUserAssisting {
    id: number;
    createdAt: string;
    updatedAt: string;
    endUserId: number;
    endUserName: string | null;
    assisting: boolean;
    incidentId: number;
    endUser: {
        id: number;
        firstName: string;
        lastName: string;
        displayName: string;
        exposureLevel: number;
        communityRole: string;
        lat: number;
        lng: number;
        level: {
            name: string;
        } | null;
        space: {
            name: string;
        } | null;
    };
}

export interface IncidentEvent {
    id: number;
    createdAt: string;
    updatedAt: string;
    type: string;
    lat: number;
    long: number;
    spaceId: number | null;
    levelId: number;
    buildingId: number | null;
    venueId: number | null;
    metadata: any | null;
    incidentId: number;
    label: string;
    eventLogType: string;
    createdBy: number;
}

/**
 * Incident duplicate record - tracks duplicate reports of the same incident
 */
export interface IncidentDuplicate {
    id: number;
    reportedBy: number;
    reportedByName: string;
    lat: number;
    long: number;
    levelId: number;
    createdAt: string;
    updatedAt: string;
}

export interface Incident {
    id: number;
    createdAt: string;
    updatedAt: string;
    status: string;
    reportedBy: number;
    reportedByName?: string | null;
    type: string;
    spaceId?: number | null;
    levelId: number;
    buildingId?: number | null;
    venueId: number;
    notes?: string | null;
    severity: number | null;
    title: string | null;
    description: string | null;
    lat: number;
    long: number;
    confirmedAt: string | null;
    resolvedAt: string | null;
    isResolvable: boolean;
    name?: string; // Display name from edge stream deltas
    drill: boolean;
    incidentCount: number;
    assistRequestSentAt: string | null;
    updated: boolean;
    originalTitle: string;
    // V2 API nested relations
    incidentZones?: IncidentZone[];
    incidentSafeZones?: IncidentSafeZone[];
    incidentComments?: IncidentComment[];
    endUsersAssisting?: EndUserAssisting[];
    incidentDuplicates?: IncidentDuplicate[];
    notifications?: any[];
    markers?: Marker[];
    events?: IncidentEvent[];
    comments?: IncidentComment[]; // Alternative field name for comments
    // Relations for display
    level?: Level;
    space?: Space;
}

// V2 Notification Types and Enums
export enum NotificationStatus {
    UNSENT = "unsent",
    SENT = "sent",
    DELIVERED = "delivered",
    ACKNOWLEDGED = "acknowledged",
}

export enum NotificationType {
    TEXT = "text",
    PUSH = "push",
    EMAIL = "email",
    ALERT = "alert",
    SYSTEM = "system",
}

export interface NotificationTemplate {
    id: number;
    createdAt: string;
    updatedAt: string;
    archived: boolean;
    isArchivable: boolean;
    name: string;
    title: string | null;
    content: string;
    appContent: string | null;
    action: string | null;
    category: string;
    type: string;
    language: string;
    displayMechanism: string | null;
    displayIconName: string | null;
    contentAvailable: string | null;
    apnsPushType: string | null;
    apnsPriority: string | null;
}

export interface NotificationRecipient {
    id: number;
    createdAt: string;
    updatedAt: string;
    status: NotificationStatus;
    resendCount: number | null;
    sentAt: string | null;
    deliveredAt: string | null;
    acknowledgedAt: string | null;
    notificationId: number;
    recipientId: number;
    displayMechanism: string;
}

export interface Notification {
    id: number;
    createdAt: string;
    updatedAt: string;
    status: NotificationStatus;
    channel: string;
    sentAt: string | null;
    acknowledgedAt: string | null;
    type: NotificationType;
    incidentId: number | null;
    templateId: number | null;
    content: string;
    appContent: string;
    template?: NotificationTemplate;
    notificationRecipients?: NotificationRecipient[];
}

// V2 Device Summary Response
export interface DeviceSummaryResponse {
    type: {
        door: number;
        camera: number;
        masterBeacon: number;
        trackingBeacon: number;
        smartBeacon: number;
        defibrillator: number;
        firstAidKit: number;
        gunshotDetector: number;
        bleedControlKit: number;
        chokingRescueDevice: number;
    };
    totalRecords: number;
    status: {
        error: number;
        online: number;
        offline: number;
        unconfigured: number;
    };
}

// Database Types - Consolidated endpoint response
export interface DatabaseTypes {
    notificationStates: string[];
    notificationTypes: string[];
    markerTypes: string[];
    incidentTypes: {
        id: number;
        name: string;
        value: string;
        resolvable: boolean;
        evacuate: boolean;
    }[];
    incidentStates: string[];
    exposureLevelTypes: string[];
    eventTypes: string[];
    deviceTypes: string[];
    deviceOperationStates: string[];
    communityRoles: CommunityRole[];
}

// V2 User Event History Types
export interface UserLocationHistoryEvent {
    id: number;
    createdAt: string;
    lat: number;
    long: number;
    level: {
        id: number;
        name: string;
    };
    building: {
        id: number;
        name: string;
    };
    space: {
        id: number;
        name: string;
    };
    venue: {
        id: number;
        name: string;
    };
}

export interface UserExposureHistoryEvent {
    id: number;
    createdAt: string;
    incidentId: number | null;
    exposureLevel: number;
    exposureSource: string;
}

export interface UserLocationHistoryResponse {
    page: number;
    pageSize: number;
    total: number;
    data: UserLocationHistoryEvent[];
}

export interface UserExposureHistoryResponse {
    page: number;
    pageSize: number;
    total: number;
    data: UserExposureHistoryEvent[];
}

// V2 User Notification History Types
export interface UserNotificationHistoryEvent {
    id: number;
    createdAt: string;
    updatedAt: string;
    deliveredAt: string | null;
    acknowledgedAt: string | null;
    status: string; // "sent", "delivered", "acknowledged", etc.
    notification: {
        id: number;
        incidentId: number | null;
        incident: {
            title: string | null;
            originalTitle: string;
            type: string;
            status: string;
        } | null;
        content: string;
        appContent: string;
        template: {
            id: number;
            name: string;
            title: string;
            action: string | null;
            category: string;
            language: string;
            displayIconName: string;
        };
    };
}

export interface UserNotificationHistoryResponse {
    page: number;
    pageSize: number;
    total: number;
    data: UserNotificationHistoryEvent[];
}

// ================================================================
// Consumer API - Group Types
// ================================================================

export interface GroupMember {
    id: number;
    userId: number;
    userKey?: string;
    groupId: number;
    joinedAt: string;
}

export interface Group {
    id: number;
    key?: string;
    avatar?: string | null;
    type?: string | null;
    metaData?: any | null;
    displayName: string;
    channelTopic?: string | null;
    description?: string | null;
    communityId: number;
    createdAt: string;
    updatedAt?: string;
    expiresAt?: string | null;
    venueId?: number | null;
    venue?: VenueAdmin | null;
    community?: Community;
    members?: GroupMember[];
    /** Set only on first responder groups; null on every ordinary group. */
    responderType?: ResponderType | null;
    address?: string | null;
    /** Dispatch line — voice only, never SMS. */
    phone?: string | null;
    email?: string | null;
}

export interface CreateGroupDto {
    displayName: string;
    communityId: number;
    avatar?: string;
    type?: string;
    channelTopic?: string;
    description?: string;
    expiresAt?: string;
}

export interface UpdateGroupDto {
    displayName?: string;
    avatar?: string;
    type?: string;
    channelTopic?: string;
    description?: string;
    expiresAt?: string;
}

/** The three outside emergency services, lowercase on the wire. */
export type ResponderType = "police" | "fire" | "ems";

/** POST /communities/:id/first-responders — communityId lives in the path. */
export interface CreateFirstResponderGroupDto {
    responderType: ResponderType;
    displayName: string;
    address: string;
    phone: string;
    email?: string;
    description?: string;
}

/** PATCH /communities/:id/first-responders/:groupId — the service is fixed at creation. */
export interface UpdateFirstResponderGroupDto {
    displayName?: string;
    address?: string;
    phone?: string;
    /** null clears the stored value; the server rejects "". */
    email?: string | null;
    description?: string | null;
}

// ================================================================
// Consumer API - Community Management Types (Admin Portal)
// ================================================================

/**
 * Venue entity from Consumer API (Admin Portal)
 * Note: This is different from the EdgeNode Venue type which includes mapping details
 */
export interface VenueAdmin {
    id: number;
    key: string;
    displayName: string;
    description?: string;
    mapSlug?: string;
    mappedInApiVersion?: string;
    mappedInClientId?: string;
    mappedInClientSecret?: string;
    // Indoor-positioning (IPS) weighting factors — CV-1618. Backend defaults each to 4.5.
    androidIpsFactor?: number;
    iosIpsFactor?: number;
    beaconIpsFactor?: number;
    // Indoor-positioning (IPS) minimum accuracy.
    minAccuracyToIgnoreGPS?: number;
    isDefault: boolean;
    communityId: number;
    // The venue's own group, created alongside it; holds the venue's members.
    groupId?: number | null;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * DTO for creating a new community
 */
export interface CreateCommunityDto {
    displayName: string;
    orgName: string;
    description?: string;
    avatar?: string;
    isPublic?: boolean;
    ownerId: number;
    orgUrl?: string;
    domainName?: string;
    uniqueId?: string;
    autoInvite?: boolean;
}

/**
 * DTO for updating an existing community
 */
export interface UpdateCommunityDto {
    displayName?: string;
    description?: string;
    avatar?: string;
    isPublic?: boolean;
    orgUrl?: string;
    domainName?: string;
    autoInvite?: boolean;
}

/**
 * DTO for creating a new venue
 */
export interface CreateVenueDto {
    displayName: string;
    description?: string;
    mapSlug: string;
    mappedInApiVersion?: string;
    mappedInClientId?: string;
    mappedInClientSecret?: string;
    // Indoor-positioning (IPS) weighting factors — CV-1618. Backend defaults each to 4.5.
    androidIpsFactor?: number;
    iosIpsFactor?: number;
    beaconIpsFactor?: number;
    // Indoor-positioning (IPS) minimum accuracy.
    minAccuracyToIgnoreGPS?: number;
    isDefault?: boolean;
}

/**
 * DTO for updating an existing venue
 */
export interface UpdateVenueDto {
    displayName?: string;
    description?: string;
    mapSlug?: string;
    mappedInApiVersion?: string;
    mappedInClientId?: string;
    mappedInClientSecret?: string;
    // Indoor-positioning (IPS) weighting factors — CV-1618. Backend defaults each to 4.5.
    androidIpsFactor?: number;
    iosIpsFactor?: number;
    beaconIpsFactor?: number;
    // Indoor-positioning (IPS) minimum accuracy.
    minAccuracyToIgnoreGPS?: number;
    isDefault?: boolean;
}

/** GET /users/email/{email} — lookup response. `claimed: false` → State C2, `true` → C1. */
export interface LookupUserResponse {
    id: number;
    displayName: string;
    email: string;
    key: string;
    /** Absent at runtime until the backend ships it — treat `undefined` as claimed (§7 V8). */
    claimed?: boolean;
}

/** POST /communities/{id}/membership/add/{userId} — §11.2. All fields optional. */
export interface AddMembershipDto {
    role?: CommunityRole;
    venueIds?: number[];
    groupIds?: number[];
    uniqueId?: string;
}

/** POST /communities/{id}/membership/update/{userId} — §12 migration. */
export interface UpdateMembershipDto {
    role?: CommunityRole;
    addVenueIds?: number[];
    addGroupIds?: number[];
    removeVenueIds?: number[];
    removeGroupIds?: number[];
}

/** POST /users/provision — §11.3. email/displayName/communityId/profile required. */
export interface ProvisionUserDto {
    email: string;
    displayName: string;
    communityId: number;
    profile: ProvisionProfileDto;
    membership?: AddMembershipDto;
}

export interface ProvisionProfileDto {
    firstName: string;
    lastName: string;
    phoneNumbers?: ProvisionPhoneDto[];
}

export interface ProvisionPhoneDto {
    mobileNumber: string;
    receiveSms: boolean;
}

/** Membership actually joined (venue default groups included, de-duplicated) — §11.2/11.3. */
export interface MembershipGroupInfo {
    id: number;
    key: string;
    displayName: string;
}

/** POST …/membership/add/{userId} → 201. Ignore externalId/externalToken (edge bookkeeping). */
export interface MembershipResponse {
    id: number;
    userId: number;
    communityId: number;
    communityRole: CommunityRole;
    uniqueId: string;
    joinedAt: string;
    groups: MembershipGroupInfo[];
}

/** POST /users/provision → 201. */
export interface ProvisionResponse {
    id: number;
    key: string;
    email: string;
    active: boolean;
    claimed: boolean;
    setPasswordEmailSent: boolean;
    membership: {
        id: number;
        communityRole: CommunityRole;
        groups: MembershipGroupInfo[];
    };
}

/** POST …/resend-setup/{userId} → 201. A 201 means the provider accepted it — claim it. */
export interface ResendSetupResponse {
    userId: number;
    email: string;
    sentAt: string;
}

// ================================================================
// Consumer API - User List Types (GET /api/v2/users)
// ================================================================

/**
 * User profile from GET /api/v2/users response
 */
export interface UserListProfile {
    id: number;
    firstName: string;
    lastName: string;
    avatar: string | null;
    isFirstAidCertified: boolean;
}

/**
 * User membership from GET /api/v2/users response
 */
export interface UserListMembership {
    id: number;
    communityId: number;
    communityRole: CommunityRole;
    /** ISO 8601 timestamp, scoped to this community (a user in several
     *  communities has a different joinedAt per membership). */
    joinedAt: string;
}

/**
 * Group association from GET /api/v2/users response
 */
export interface UserListGroup {
    id: number;
    groupId: number;
    joinedAt?: string;
}

/**
 * User item from GET /api/v2/users response
 */
export interface UserListItem {
    id: number;
    key: string;
    displayName: string;
    email: string;
    active: boolean;
    disabled: boolean;
    /** Drives the §07 list chip. Optional until the backend ships it (see §7 V8). */
    claimed?: boolean;
    role: string; // Global role: "Admin", "Developer", "EndUser", etc.
    profile: UserListProfile;
    memberships: UserListMembership[];
    groups: UserListGroup[];
}

/**
 * Paginated response from GET /api/v2/users
 */
export interface UserListResponse {
    page: number;
    pageSize: number;
    total: number;
    data: UserListItem[];
}

/**
 * Skyward SIS integration client for a community. The backend never returns
 * `key`/`secret`/`accessToken`/`tokenExpiry` — see safety-guard-consumer-server's
 * src/skyward/README.md for why.
 */
export interface SkywardClient {
    id: number;
    name: string;
    url: string;
    hasUniqueEmails: boolean;
    communityId: number;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * DTO for creating a Skyward client. communityId comes from the route, not the body.
 */
export interface CreateSkywardClientDto {
    name: string;
    key: string;
    secret: string;
    url: string;
    hasUniqueEmails: boolean;
}

/**
 * DTO for updating a Skyward client. `secret` is write-only — omit it to leave the
 * stored secret unchanged; never send an empty string.
 */
export interface UpdateSkywardClientDto {
    name?: string;
    key?: string;
    secret?: string;
    url?: string;
    hasUniqueEmails?: boolean;
}

/**
 * A school reported by Skyward that isn't yet assigned to a venue.
 */
export interface SkywardSchoolSummary {
    sourcedId: string;
    name: string;
    type: string;
}

/**
 * A school Skyward has been assigned to one of the community's venues.
 */
export interface SkywardAssignedSchool {
    id: number;
    sourcedId: string;
    name: string;
    venueId: number;
    skywardClientId: number;
}

export interface SkywardSchoolsResponse {
    availableSchools: SkywardSchoolSummary[];
    assignedSchools: SkywardAssignedSchool[];
}

/**
 * DTO for assigning a school to a venue. skywardClientId comes from the route.
 */
export interface AssignSkywardSchoolDto {
    schoolSourceId: string;
    venueKey: string;
}

/**
 * DTO for the ad-hoc (pre-save) test-connection check used by the create form.
 */
export interface TestConnectionDto {
    url: string;
    key: string;
    secret: string;
}

export interface TestConnectionResult {
    success: boolean;
    message?: string;
}
